import {
  BillingLicensesModalViewModelQuery,
  useBillingCompanyBillingInfoQuery,
  useBillingLicensesModalViewModelQuery,
} from 'domain/api/graphql/generated';
import { FetchPolicyKeys } from 'domain/api/graphql/types';

import useSubscriptionCost from 'domain/billing/useSubscriptionCost';
import useLicensesSuspendAutoRenewal from 'domain/billing/useLicensesSuspendAutoRenewal';
import useLicensesResumeAutoRenewal from 'domain/billing/useLicensesResumeAutoRenewal';
import useBindCard from 'domain/billing/useBindCard';
import useBindCardAndPay from 'domain/billing/useBindCardAndPay';

type BillingData = {
  companyBillingInfoQuery: NonNullable<BillingLicensesModalViewModelQuery['companyBillingInfoQuery']>;
  organizationCurrentPricePlanQuery: NonNullable<{
    organizationId: number;
    pricePlan: { id: string };
  }>;
  paymentMethodPureQuery: NonNullable<BillingLicensesModalViewModelQuery['paymentMethodPureQuery']>;
  acceptanceAdvancedQuery: BillingLicensesModalViewModelQuery['acceptanceAdvancedQuery'];
};

const isDataValid = (data?: BillingLicensesModalViewModelQuery): data is BillingData => {
  const hasBillingInfo = !!data?.companyBillingInfoQuery;
  const hasPricePlan = !!data?.organizationCurrentPricePlanQuery?.pricePlan?.id;
  const hasPaymentMethod = data?.paymentMethodPureQuery?.paymentMethod !== undefined;

  return hasBillingInfo && hasPricePlan && hasPaymentMethod;
};

export const useBillingData = (companyId: string) => {
  const { request: getLinkForBindingCard } = useBindCard();
  const { request: getLinkForPayment } = useBindCardAndPay();
  const [requestCost, { loading: subscriptionCostLoading }] = useSubscriptionCost();
  const { request: suspendAutoRenewal, result: suspendAutoRenewalResult } = useLicensesSuspendAutoRenewal();
  const { request: resumeAutoRenewal, result: resumeAutoRenewalResult } = useLicensesResumeAutoRenewal();
  const { refetch } = useBillingCompanyBillingInfoQuery({
    fetchPolicy: FetchPolicyKeys.NETWORK_ONLY,
    variables: { companyId },
  });

  const { data, loading, error } = useBillingLicensesModalViewModelQuery({
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
    variables: { companyId },
    notifyOnNetworkStatusChange: true,
  });

  if (loading || error || !isDataValid(data)) return null;

  const billingInfo = data.companyBillingInfoQuery;
  const pricePlan = data.organizationCurrentPricePlanQuery.pricePlan;
  const paymentMethodData = data.paymentMethodPureQuery;

  const {
    status,
    hasTrialLicenses,
    hasInactiveLicenses,
    hasActiveLicenses,
    hasWaitPaymentLicenses,
    hasGraceLicenses,
    unpaidLicenses,
    nonRenewableLicenses,
    totalLicenses,
    selectedPaymentKind,
  } = billingInfo;

  return {
    billingInfo,
    pricePlan,
    paymentMethodData,

    status,
    hasTrialLicenses,
    hasInactiveLicenses,
    hasActiveLicenses,
    hasWaitPaymentLicenses,
    hasGraceLicenses,
    unpaidLicenses,
    nonRenewableLicenses,
    totalLicenses,
    unpaidLicensesIds: unpaidLicenses.map((item) => item.id),
    currentPricePlanPeriodId: pricePlan.id,
    paymentMethod: paymentMethodData.paymentMethod ?? null,
    isLinkingInProcess: paymentMethodData.isLinkingInProcess ?? false,
    selectedPaymentKind,
    acceptanceAdvanceStatus: !data.acceptanceAdvancedQuery ? null : data.acceptanceAdvancedQuery.status,

    getLinkForBindingCard,
    getLinkForPayment,
    requestCost,
    subscriptionCostLoading,
    suspendAutoRenewal,
    suspendAutoRenewalResult,
    resumeAutoRenewal,
    resumeAutoRenewalResult,
    refetchBillingInfo: refetch,
  };
};
